import React from 'react';
import { cn } from '../../../lib/utils';
import { Card } from '../../../components/ui/card';
import { Loader2 } from 'lucide-react';
import { CashflowCardsProps } from '../types';

/** Льготный период: комиссию начнут удерживать только с будущей даты. */
const isCommissionFree = (provider: { commissionFrom: string | null }) =>
  Boolean(provider.commissionFrom) &&
  provider.commissionFrom! > new Date().toISOString().slice(0, 10);

/**
 * Карточка провайдера: собрано → чистое → поступило → осталось получить.
 * Последняя строка — долг за всё время, поэтому она отбита от остальных.
 */
export function CashflowCards({
  isDark,
  providers,
  total,
  isLoading,
  formatMoney,
}: CashflowCardsProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  const cardCls = cn(
    'rounded-2xl border-0 p-5 transition-all duration-300',
    isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
  );
  const labelCls = cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500');
  const valueCls = cn('text-sm font-semibold', isDark ? 'text-white' : 'text-[#111111]');

  const row = (
    label: string,
    value: number,
    tone?: 'net' | 'outstanding',
    currency?: string,
  ) => (
    <div className="flex items-center justify-between gap-3">
      <span className={labelCls}>{label}</span>
      <span
        className={cn(
          valueCls,
          tone === 'net' && 'text-emerald-500',
          tone === 'outstanding' && (value > 0 ? 'text-amber-500' : 'text-emerald-500'),
        )}
      >
        {formatMoney(value, currency)}
      </span>
    </div>
  );

  const totalCell = (label: string, value: number, tone?: 'net' | 'outstanding') => (
    <div>
      <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{label}</p>
      <p
        className={cn(
          'text-lg lg:text-xl font-bold mt-1',
          tone === 'net' && 'text-emerald-500',
          tone === 'outstanding' && (value > 0 ? 'text-amber-500' : 'text-emerald-500'),
          !tone && (isDark ? 'text-white' : 'text-[#111111]'),
        )}
      >
        {formatMoney(value, 'UZS')}
      </p>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Итог — широкой полосой: это главное число экрана, его читают первым. */}
      <Card className={cn(cardCls, 'ring-1 ring-[#C9A227]/40')}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
          <div className="lg:w-40 shrink-0">
            <h3 className={cn('text-lg font-bold', isDark ? 'text-white' : 'text-[#111111]')}>
              Итого
            </h3>
            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
              все провайдеры, в сумах
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
            {totalCell('Собрано', total.gross)}
            {totalCell('Чистое', total.net, 'net')}
            {totalCell('Поступило', total.settled)}
            {totalCell('Осталось получить', total.outstanding, 'outstanding')}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {providers.map((provider) => (
          <Card key={provider.providerId} className={cardCls}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={cn('text-lg font-bold', isDark ? 'text-white' : 'text-[#111111]')}>
                {provider.name}
              </h3>
              {isCommissionFree(provider) ? (
                <span
                  className="text-xs px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-500"
                  title={`Комиссия ${provider.commissionPercent}% с ${provider.commissionFrom}`}
                >
                  без комиссии
                </span>
              ) : (
                <span
                  className={cn(
                    'text-xs px-2 py-1 rounded-lg',
                    isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500',
                  )}
                >
                  −{provider.commissionPercent}%
                </span>
              )}
            </div>
            <div className="space-y-2">
              {row('Собрано', provider.gross, undefined, provider.currency)}
              {row('Чистое', provider.net, 'net', provider.currency)}
              {row('Поступило', provider.settled, undefined, provider.currency)}
            </div>
            <div className={cn('mt-4 pt-3 border-t', isDark ? 'border-slate-700' : 'border-slate-200')}>
              {row('Осталось получить', provider.outstanding, 'outstanding', provider.currency)}
              {provider.currency !== 'UZS' && (
                <p className={cn('text-xs mt-1 text-right', isDark ? 'text-slate-500' : 'text-slate-400')}>
                  {provider.rate > 0
                    ? `${formatMoney(provider.outstandingUzs, 'UZS')} по курсу ${provider.rate}`
                    : 'курс не задан'}
                </p>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
